import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { MapTo } from '@adobe/aem-react-editable-components'
import DynamicProductCarousel from '@src/components/DynamicProductCarousel'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import { useBestSellerProductsQuery as useBuyAgainProductsQuery } from '@src/queries/BestSellerProductsQuery.generated'
import { useBestSellerProductsByRegionQuery } from '@src/queries/BestSellerProductsByRegionQuery.generated'
import { recommendationProductsConst } from '@src/aem-content/aemUtils'
import { useCurrentUser } from '@utils/useCurrentUser'
import { ErpType } from '@utils/useProductErpType'
import { AemContext } from '@src/aem-content/components/AemContext'
import { useCountriesData } from '@src/utils/useCountriesData'
import { useUserSession } from '@src/utils/useUserSession'
import SkeletonLoader from '../SkeletonLoader'

const useStyles = makeStyles((theme: Theme) => ({
  carouselWrapper: {
    [theme.breakpoints.down('xs')]: {
      touchAction: 'pan-y',
    },
    '@media (max-width: 1350px)': {
      padding: theme.spacing(0, 5),
    },
  },
  error: {
    padding: theme.spacing(6, 5, 3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  contentCondensed: {
    padding: 0,
  },
}))

const ProductRecommendationCarousel: React.FC<any> = ({ typeId, headline }) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { isBlueB2BUser, userId, userIsLoggedIn } = useCurrentUser()
  const { userSession } = useUserSession()
  const { isBuyAgainEmpty, setIsBuyAgainEmpty } = useContext(AemContext)
  const { setShowHorizontaltabLoader } = useContext(AemContext)
  const { getCountryProp } = useCountriesData()
  const [fetchedProducts, setFetchedProducts] = useState<CarouselProduct[]>([])

  // Note: Checking productRecommendationCarousel is for homePage buy again products.
  const isBuyAgainType: boolean = typeId === 'buyAgain'
  // Note: Checking productRecommendationCarousel is for homePage bestsellers products.
  const isBestSellersType: boolean = typeId === 'bestsellers'
  // Note: Checking productRecommendationCarousel is for homePage popular products.
  const isPopularProductsType: boolean = typeId === 'popular_products'
  // Note: Checking productRecommendationCarousel is for category page bestSellers products.
  const isCatPageBestSellersAPI: boolean =
    recommendationProductsConst[typeId]?.typeId === 'catPageBestSellersAPI'

  // Note: Below function helps to create region wise display code for both homePageBestSellersAPI & catPageBestSellersAPI.
  // Sample Url: https://expanded-nebula-754.appspot.com/deep?get=homePageBestSellersAPI&guid=GA1.2.1607803001.1571084413&language=en&count=10&disp=NA
  // APAC for Asia & Australia
  // NA for North America
  // EMEA for Europe
  // LATAM  for South America

  // Sample Url: https://cloud-analytics.sial.com/deep?get=catPageBestSellersAPI&guid=GA1.2.1607803001.1571084413&country=US&language=en&count=10&disp=S138_APAC
  // S138_APAC for Asia & Australia
  // S138_NA for North America
  // S138_EMEA for Europe
  // S138_LATAM  for South America

  const getRegionCode = (isCatPageBestSellersDisp: boolean = false): string => {
    const regionCode: string = getCountryProp(userSession.country, 'regionCode')
    const disp =
      isCatPageBestSellersDisp && recommendationProductsConst[typeId]?.disp
        ? `${recommendationProductsConst[typeId]?.disp}_`
        : ''
    const regions: { [key: string]: string } = {
      AP: 'APAC',
      EU: 'EMEA',
      AF: 'EMEA',
      SA: 'LATAM',
    }

    const region = regions[regionCode] || regionCode
    return `${disp}${region}`
  }

  // Note: Below function helps to create typeId conditionally for homePageBestSellersAPI, homePagePopularProductsAPI & catPageBestSellersAPI.
  const getTypeId = (): string => {
    let inputTypeId: string = ''
    if (isBestSellersType || isPopularProductsType || isCatPageBestSellersAPI)
      inputTypeId = recommendationProductsConst[typeId]?.typeId || ''
    return inputTypeId
  }

  // Note: Below function helps to create displayCode conditionally for homePageBestSellersAPI, homePagePopularProductsAPI & catPageBestSellersAPI.
  const getDisplay = (): string => {
    let display: string = ''
    if (isBestSellersType || isCatPageBestSellersAPI)
      display = isCatPageBestSellersAPI ? getRegionCode(true) : getRegionCode()
    if (isPopularProductsType)
      display = recommendationProductsConst[typeId]?.disp || ''
    return display
  }

  // Note: Determines the human readable title of the carousel to show.
  const getTitle = (
    headline: string | undefined,
    isBuyItAgain: boolean | undefined
  ) => {
    if (headline && isBuyItAgain) {
      return formatMessage({
        id: 'POPULAR_PRODUCTS',
        defaultMessage: 'Popular Products',
      })
    }

    if (headline) {
      return headline
    }

    return <></>
  }

  /**
   * The type of products/product card set for Dynamic Product Carousel needs
   * to be resolved from whatever type the API for any given product recommendation type supplies.
   * These APIs supply a large range of product "type" sets.
   * Where we can we resolve the "normal" Dynamic Product Carousel types for analytics purposes.
   * Where we can't align the type of carousel we use whatever the API supplies and format the string...
   * to match the other string format of other carousel types.
   *
   * @param inputTypeId
   * @param isBuyAgain
   * @returns
   */
  const getCarouselType = (inputTypeId: string, isBuyAgain: boolean) => {
    if (isBuyAgain) {
      return CarouselProductTypes.PopularProducts
    }

    return (
      recommendationProductsConst?.[inputTypeId]?.carouselProductType ||
      recommendationProductsConst?.[inputTypeId]?.disp
        ?.toLowerCase()
        ?.replace(/[-_]/g, ' ')
    )
  }

  // Note: Below function helps to set querySkip boolean value to false only for homePageBestSellersAPI, homePagePopularProductsAPI & catPageBestSellersAPI.
  // and this function value is used to skip other query calls except for homePageBestSellersAPI, homePagePopularProductsAPI & catPageBestSellersAPI
  const querySkip = (): boolean =>
    !(
      isBestSellersType ||
      (isPopularProductsType && !userIsLoggedIn) ||
      isCatPageBestSellersAPI
    )

  const { loading, error, data } = useBuyAgainProductsQuery({
    ssr: false,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
        typeId: recommendationProductsConst[typeId]?.typeId,
        disp: recommendationProductsConst[typeId]?.disp,
        ...(isBuyAgainType ? { participant_id: userId } : {}),
        //NOTE - This id helps testing buy again as we are certain to get products for the below participant_id.
        //...(typeId === 'buyAgain' ? {participant_id: '0116a670-9a68-11eb-ab02-d9c6651681af'} : {})
      },
    },
    skip:
      isBestSellersType ||
      isPopularProductsType ||
      (isBuyAgainType && !userIsLoggedIn) ||
      isCatPageBestSellersAPI,
  })

  // Note: The query below will be called conditionally only for homePagePopularProductsAPI if buyAgain api don't have any response for loggedin user.
  const {
    data: buyAgainFallbackPopularProductsData,
    loading: buyAgainFallbackpopularProductsLoading,
    error: buyAgainFallbackpopularProductsError,
  } = useBestSellerProductsByRegionQuery({
    variables: {
      input: {
        typeId: recommendationProductsConst['popular_products'].typeId,
        disp: recommendationProductsConst['popular_products'].disp,
      },
    },
    ssr: false,
    fetchPolicy: 'network-only',
    skip: !isBuyAgainEmpty,
  })

  // Note: The query below will be called only for homePageBestSellersAPI, homePagePopularProductsAPI, and catPageBestSellersAPI.
  const {
    data: bestSellingProductsData,
    loading: bestSellingProductsLoading,
    error: bestSellingProductsError,
  } = useBestSellerProductsByRegionQuery({
    variables: {
      input: {
        typeId: getTypeId(),
        disp: getDisplay(),
      },
    },
    ssr: false,
    fetchPolicy: 'network-only',
    skip: querySkip(),
  })

  useEffect(() => {
    if (
      data &&
      !data?.getBestSellerProducts?.products?.length &&
      isBuyAgainType
    ) {
      setIsBuyAgainEmpty(true)
    }
  }, [data])

  useEffect(() => {
    if (data && data.getBestSellerProducts?.products.length) {
      const filteredProducts =
        data?.getBestSellerProducts?.products?.filter(Boolean)

      setFetchedProducts(filteredProducts)
    }

    if (
      bestSellingProductsData &&
      bestSellingProductsData.getBestSellerProductsByRegion?.products.length
    ) {
      setFetchedProducts(
        bestSellingProductsData?.getBestSellerProductsByRegion?.products
      )
    }

    if (
      buyAgainFallbackPopularProductsData &&
      buyAgainFallbackPopularProductsData.getBestSellerProductsByRegion
        ?.products.length
    ) {
      setFetchedProducts(
        buyAgainFallbackPopularProductsData?.getBestSellerProductsByRegion
          ?.products
      )
    }
  }, [data, bestSellingProductsData, buyAgainFallbackPopularProductsData])

  useEffect(() => {
    setShowHorizontaltabLoader(
      loading ||
        bestSellingProductsLoading ||
        buyAgainFallbackpopularProductsLoading
    )
  }, [
    loading,
    bestSellingProductsLoading,
    buyAgainFallbackpopularProductsLoading,
  ])

  if (
    loading ||
    bestSellingProductsLoading ||
    buyAgainFallbackpopularProductsLoading
  ) {
    return <SkeletonLoader count={4} />
  }

  // If any of the API fetches fail console log the error and then show a something went wrong component.
  if (
    error ||
    bestSellingProductsError ||
    buyAgainFallbackpopularProductsError
  ) {
    const thrownError =
      error || bestSellingProductsError || buyAgainFallbackpopularProductsError

    console.error(thrownError)

    return (
      <div className={classes.error}>
        <ErrorIcon />
        <span>Something went wrong, please try again.</span>
      </div>
    )
  }

  // If all APIs respond but lack for relevant product data just return nothing.
  if (
    data?.getBestSellerProducts?.products?.length &&
    bestSellingProductsData?.getBestSellerProductsByRegion?.products?.length &&
    buyAgainFallbackPopularProductsData?.getBestSellerProductsByRegion?.products
      .length
  ) {
    return null
  }

  if (
    fetchedProducts &&
    fetchedProducts?.length > 0 &&
    Array.isArray(fetchedProducts)
  ) {
    const isValidBuyItAgain = isBuyAgainType && isBuyAgainEmpty

    return (
      <div
        id={`aem-recommended-products-${recommendationProductsConst[typeId]?.disp}`}
        className={classes.carouselWrapper}
      >
        <DynamicProductCarousel
          title={getTitle(headline, isValidBuyItAgain)}
          type={getCarouselType(typeId, isValidBuyItAgain)}
          products={fetchedProducts}
          analyticsPageName={
            isCatPageBestSellersAPI ? 'categorypage' : 'homepage'
          }
          analyticsPageDetails={
            isCatPageBestSellersAPI ? 'categorypage' : 'homepage'
          }
          isProductRecommendation
        />
      </div>
    )
  }

  // If no conditions are met for errors or carousel rendering just return empty.
  return null
}

export default MapTo(
  'cms-commons/components/content/productRecommendationCarousel'
)(ProductRecommendationCarousel)
